import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import { useState } from 'react';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  ExecuteContext,
  BaseAction,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { coco, llm, type ModelId } from '@cotera/api';
import { CocoEvent, cocoActionMetadata } from '@cotera/client/app/etc';
import { useWhoami } from '@cotera/client/app/stores/org';
import { useGatedModelFilter } from '@cotera/client/app/hooks/use-gated-models';

const COCO_MODEL_OVERRIDE_KEY = 'COTERA_COCO_MODEL_OVERRIDE';

export const getCocoModelOverride = (): ModelId | null => {
  const value = localStorage.getItem(COCO_MODEL_OVERRIDE_KEY);
  if (value !== null && value in llm.MODELS) {
    return value as ModelId;
  }
  return null;
};

export const setCocoModelOverride = (value: ModelId | null): void => {
  if (value === null) {
    localStorage.removeItem(COCO_MODEL_OVERRIDE_KEY);
  } else {
    localStorage.setItem(COCO_MODEL_OVERRIDE_KEY, value);
  }
};

type Params = {
  model: ModelId | null;
};

export class SwitchCocoModelAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = "Switch Coco's base model";
  description = "Switch Coco's base model";
  inputSchema = z.object({
    model: z.string().nullable(),
  }) as unknown as z.ZodSchema<Params>;
  group = 'app';
  icon = 'bot';

  private _previousModel: ModelId | null = null;

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  applicable(_context: ApplicableContext): boolean {
    return true;
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  async execute(
    payload: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    this._previousModel = getCocoModelOverride();
    setCocoModelOverride(payload.model);
    return ok({});
  }

  track({
    payload,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    return cocoActionMetadata(CocoEvent.ModelSwitched, {
      fromModel: this._previousModel,
      toModel: payload.model,
    });
  }

  ask(_params: Partial<Params>, _context: ExecuteContext): AskResponse<Params> {
    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <SwitchCocoModelAskView
              currentModel={getCocoModelOverride()}
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

// The subsidized GPT-5.5 variant is intentionally `active: false` so it stays
// hidden from end-user model pickers (V0 chat input, V0 LLM column). This
// switcher is gated by the `coco-is-kimi` feature flag — internal operators
// only — so we surface the subsidized id explicitly here. It lets kimi users
// pin their override to subsidized rather than relying on `coco.DEFAULT_MODEL`
// staying pointed at it.
const ELIGIBLE_MODELS = (Object.values(llm.MODELS) as llm.Model[]).filter(
  (m) =>
    (m.active || m.id === llm.LlmModels.azureFoundry.AzureGpt5Dot5Subsidized) &&
    m.supports.toolUse &&
    m.supports.streaming
);

const MODEL_GROUPS = [
  { provider: 'Anthropic' as const, label: 'Anthropic' },
  { provider: 'OpenAI' as const, label: 'OpenAI' },
  { provider: 'Google' as const, label: 'Google' },
  { provider: 'AzureFoundry' as const, label: 'Cotera' },
  { provider: 'LunaRoute' as const, label: 'Cotera Unlimited' },
] as const;

const SwitchCocoModelAskView: React.FC<{
  currentModel: ModelId | null;
  onRespond: (params: Params) => Promise<void>;
}> = ({ currentModel, onRespond }) => {
  const org = useWhoami((s) => s.org);
  // Same feature-flag filter every other picker applies.
  const showModel = useGatedModelFilter();
  // Hide models the org isn't allowed to use (no-op for unrestricted orgs), and
  // list superseded versions under "Other models", by vendor.
  const { current: eligibleModels, legacyByVendor } =
    llm.groupLegacyModelsByVendor(
      llm
        .allowedModelsForOrg(org.id, ELIGIBLE_MODELS)
        .filter((m) => showModel(m))
    );
  const [selectedModel, setSelectedModel] = useState<ModelId | 'default'>(
    currentModel ?? 'default'
  );

  const handleSubmit = async () => {
    await onRespond({
      model: selectedModel === 'default' ? null : selectedModel,
    });
  };

  return (
    <ModalPageView
      title="Switch Coco's base model"
      subtitle="Choose which model Coco uses for new conversations"
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          await handleSubmit();
        }}
      >
        <Select
          value={selectedModel}
          onValueChange={(v: string) =>
            setSelectedModel(v as ModelId | 'default')
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a model" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">
              Default ({llm.MODELS[coco.DEFAULT_MODEL].name})
            </SelectItem>
            {[
              ...MODEL_GROUPS.map(({ provider, label }) => ({
                key: provider,
                label,
                models: eligibleModels.filter((m) => m.provider === provider),
              })),
              ...legacyByVendor.map(({ vendor, models }) => ({
                key: `other-${vendor}`,
                label: `Other models · ${vendor}`,
                models,
              })),
            ].map(({ key, label, models }) => {
              if (models.length === 0) {
                return null;
              }
              return (
                <SelectGroup key={key}>
                  <SelectLabel>{label}</SelectLabel>
                  {models.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              );
            })}
          </SelectContent>
        </Select>

        <div className="w-full flex justify-end gap-2">
          <Button type="submit" variant="default" className="w-fit self-end">
            Switch Model
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
